import axios from 'axios'

const api = axios.create({
  baseURL: '/api',
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor for adding auth token and tenant_id
api.interceptors.request.use(
  (config) => {
    const authStorage = localStorage.getItem('auth-storage')
    if (authStorage) {
      const { state } = JSON.parse(authStorage)
      if (state.token) {
        config.headers.Authorization = `Bearer ${state.token}`
      }

      // Auto-add tenant_id for super_admin requests
      if (state.user?.role === 'super_admin' && state.selectedTenant?.id) {
        const url = config.url || ''
        // Exclude global endpoints that don't need tenant filtering
        const globalEndpoints = ['/auth/', '/tenants/', '/system/', '/users/']
        const isGlobalEndpoint = globalEndpoints.some(ep => url.startsWith(ep))

        if (!isGlobalEndpoint && !config.params?.tenant_id) {
          config.params = {
            ...config.params,
            tenant_id: state.selectedTenant.id
          }
        }
      }
    }
    return config
  },
  (error) => Promise.reject(error)
)

// Response interceptor for handling errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      // Token expired, try to refresh
      const authStorage = localStorage.getItem('auth-storage')
      if (authStorage) {
        const { state } = JSON.parse(authStorage)
        if (state.refreshToken) {
          try {
            const response = await axios.post('/api/auth/refresh', {
              refresh_token: state.refreshToken
            })
            const { access_token, refresh_token } = response.data
            const newState = { ...state, token: access_token, refreshToken: refresh_token }
            localStorage.setItem('auth-storage', JSON.stringify({ state: newState }))

            // Retry the original request
            error.config.headers.Authorization = `Bearer ${access_token}`
            return axios(error.config)
          } catch {
            // Refresh failed, logout
            localStorage.removeItem('auth-storage')
            window.location.href = '/login'
          }
        }
      }
    }
    return Promise.reject(error)
  }
)

export default api

// API Functions - All paths end with / to match FastAPI routes
export const extensionsApi = {
  list: (params?: Record<string, unknown>) => api.get('/extensions/', { params }),
  get: (id: string) => api.get(`/extensions/${id}`),
  create: (data: Record<string, unknown>, tenantId?: string) =>
    api.post('/extensions/', data, tenantId ? { params: { tenant_id: tenantId } } : undefined),
  update: (id: string, data: Record<string, unknown>) => api.put(`/extensions/${id}`, data),
  delete: (id: string) => api.delete(`/extensions/${id}`),
  bulkCreate: (data: Record<string, unknown>, tenantId?: string) =>
    api.post('/extensions/bulk', data, tenantId ? { params: { tenant_id: tenantId } } : undefined),
  clone: (id: string, data: { new_extension: string; new_name: string; new_password?: string }) =>
    api.post(`/extensions/${id}/clone`, data),
  toggleDnd: (id: string, enabled: boolean) =>
    api.put(`/extensions/${id}/dnd`, null, { params: { enabled } }),
  getCfStatus: (id: string) =>
    api.get(`/extensions/${id}/cf-status`),
  syncCfFromPhone: (id: string) =>
    api.post(`/extensions/${id}/cf-sync`),
  voicemailEnabled: (tenantId?: string) =>
    api.get('/extensions/voicemail-enabled', { params: tenantId ? { tenant_id: tenantId } : {} }),
}

export const trunksApi = {
  list: (params?: Record<string, unknown>) => api.get('/trunks/', { params }),
  get: (id: string) => api.get(`/trunks/${id}`),
  create: (data: Record<string, unknown>, tenantId?: string) =>
    api.post('/trunks/', data, { params: { tenant_id: tenantId } }),
  update: (id: string, data: Record<string, unknown>) => api.put(`/trunks/${id}`, data),
  delete: (id: string) => api.delete(`/trunks/${id}`),
  getStatus: (id: string) => api.get(`/trunks/${id}/status`),
  getLogs: (id: string, params?: { limit?: number; level?: string }) =>
    api.get(`/trunks/${id}/logs`, { params }),
  clearLogs: (id: string) => api.delete(`/trunks/${id}/logs`),
  testRegistration: (id: string) => api.post(`/trunks/${id}/test-registration`),
  diagnostics: (id: string) => api.get(`/trunks/${id}/diagnostics`),
}

export const ivrApi = {
  list: (params?: Record<string, unknown>) => api.get('/ivr/', { params }),
  get: (id: string) => api.get(`/ivr/${id}`),
  create: (data: Record<string, unknown>, tenantId?: string) =>
    api.post('/ivr/', data, { params: { tenant_id: tenantId } }),
  update: (id: string, data: Record<string, unknown>) => api.put(`/ivr/${id}`, data),
  delete: (id: string) => api.delete(`/ivr/${id}`),
}

export const recordingsApi = {
  list: (params?: Record<string, unknown>) => api.get('/recordings/', { params }),
  get: (id: string) => api.get(`/recordings/${id}`),
  update: (id: string, data: Record<string, unknown>) => api.put(`/recordings/${id}`, data),
  upload: (formData: FormData) => api.post('/recordings/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  uploadMicrophone: (formData: FormData) => api.post('/recordings/from-microphone', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  startExtensionRecording: (formData: FormData) => api.post('/recordings/from-extension', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  reRecordViaExtension: (id: string, formData: FormData) => api.post(`/recordings/${id}/re-record`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  playOnExtension: (id: string, formData: FormData) => api.post(`/recordings/${id}/play-on-extension`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  checkRecordingStatus: (id: string) => api.get(`/recordings/pending/${id}/status`),
  delete: (id: string) => api.delete(`/recordings/${id}`),
  download: (id: string) => api.get(`/recordings/${id}/download`, { responseType: 'blob' }),
  // Get play URL with auth token for direct audio streaming
  getPlayUrl: (id: string, version?: string | number) => {
    const v = version ?? Date.now()
    const authStorage = localStorage.getItem('auth-storage')
    if (authStorage) {
      const { state } = JSON.parse(authStorage)
      if (state.token) {
        return `/api/recordings/${id}/play?token=${encodeURIComponent(state.token)}&v=${encodeURIComponent(String(v))}`
      }
    }
    return `/api/recordings/${id}/play?v=${encodeURIComponent(String(v))}`
  },
  // TTS endpoints
  ttsLanguages: () => api.get('/recordings/tts/languages'),
  ttsPreview: (formData: FormData) => api.post('/recordings/tts/preview', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    responseType: 'blob'
  }),
  ttsGenerate: (data: { text: string; name: string; language: string; category: string }) =>
    api.post('/recordings/tts/generate', data),
  regenerateTts: (id: string, data: { text?: string; language?: string }) =>
    api.post(`/recordings/${id}/regenerate-tts`, data),
}

export const tenantsApi = {
  list: (params?: Record<string, unknown>) => api.get('/tenants/', { params }),
  get: (id: string) => api.get(`/tenants/${id}`),
  create: (data: Record<string, unknown>) => api.post('/tenants/', data),
  update: (id: string, data: Record<string, unknown>) => api.put(`/tenants/${id}`, data),
  delete: (id: string) => api.delete(`/tenants/${id}`),
  stats: (id: string) => api.get(`/tenants/${id}/stats`),
  domainChangeImpact: (id: string, newDomain: string) => api.get(`/tenants/${id}/domain-change-impact`, { params: { new_domain: newDomain } }),
}

export const usersApi = {
  list: (params?: Record<string, unknown>) => api.get('/users/', { params }),
  get: (id: string) => api.get(`/users/${id}`),
  create: (data: Record<string, unknown>) => api.post('/users/', data),
  update: (id: string, data: Record<string, unknown>) => api.put(`/users/${id}`, data),
  delete: (id: string) => api.delete(`/users/${id}`),
}

export const cdrApi = {
  list: (params?: Record<string, unknown>) => api.get('/cdr/', { params }),
  grouped: (params?: Record<string, unknown>) => api.get('/cdr/grouped', { params }),
  stats: (params?: Record<string, unknown>) => api.get('/cdr/stats', { params }),
}

export const systemApi = {
  status: () => api.get('/system/status'),
  reload: () => api.post('/system/reload'),
  settings: () => api.get('/system/settings'),
  updateSetting: (key: string, value: string) => api.put(`/system/settings/${key}`, null, { params: { value } }),
  // QR code for softphone configuration
  getQrCodeExtensions: () => api.get('/system/qr-codes/list'),
  getSoftphoneTypes: () => api.get('/system/softphone-types'),
  generateQrCode: (extensionId: string, serverIp: string, transport: string = 'UDP', softphone: string = 'linphone', provisioningUrl?: string) =>
    api.get(`/system/qr-code/${extensionId}`, {
      params: {
        server_ip: serverIp,
        transport,
        softphone,
        format: 'base64',
        ...(provisioningUrl && { provisioning_url: provisioningUrl })
      }
    }),
  sendQrCodeEmail: (extensionId: string, serverIp: string, transport: string, softphone: string, provisioningUrl?: string, toEmail?: string) =>
    api.post(`/system/qr-code/${extensionId}/send-email`, null, {
      params: {
        server_ip: serverIp,
        transport,
        softphone,
        ...(provisioningUrl && { provisioning_url: provisioningUrl }),
        ...(toEmail && { to_email: toEmail })
      }
    }),
  // Asterisk Logs
  getLogFiles: () => api.get('/system/logs/files'),
  viewLog: (params: { file?: string; lines?: number; search?: string; level?: string; since?: string }) =>
    api.get('/system/logs/view', { params }),
  getLiveLog: (file: string = 'messages', lines: number = 50) =>
    api.get('/system/logs/live', { params: { file, lines } }),
}

export const ringGroupsApi = {
  list: (params?: Record<string, unknown>) => api.get('/ring-groups/', { params }),
  get: (id: string) => api.get(`/ring-groups/${id}`),
  create: (data: Record<string, unknown>, tenantId?: string) =>
    api.post('/ring-groups/', data, { params: { tenant_id: tenantId } }),
  update: (id: string, data: Record<string, unknown>) => api.put(`/ring-groups/${id}`, data),
  delete: (id: string) => api.delete(`/ring-groups/${id}`),
}

export const queuesApi = {
  list: (params?: Record<string, unknown>) => api.get('/queues/', { params }),
  get: (id: string) => api.get(`/queues/${id}`),
  create: (data: Record<string, unknown>, tenantId?: string) =>
    api.post('/queues/', data, { params: { tenant_id: tenantId } }),
  update: (id: string, data: Record<string, unknown>) => api.put(`/queues/${id}`, data),
  delete: (id: string) => api.delete(`/queues/${id}`),
}

export const inboundRoutesApi = {
  list: (params?: Record<string, unknown>) => api.get('/inbound-routes/', { params }),
  get: (id: string) => api.get(`/inbound-routes/${id}`),
  create: (data: Record<string, unknown>, tenantId?: string) =>
    api.post('/inbound-routes/', data, { params: { tenant_id: tenantId } }),
  update: (id: string, data: Record<string, unknown>) => api.put(`/inbound-routes/${id}`, data),
  delete: (id: string) => api.delete(`/inbound-routes/${id}`),
}

export const outboundRoutesApi = {
  list: (params?: Record<string, unknown>) => api.get('/outbound-routes/', { params }),
  get: (id: string) => api.get(`/outbound-routes/${id}`),
  create: (data: Record<string, unknown>, tenantId?: string) =>
    api.post('/outbound-routes/', data, { params: { tenant_id: tenantId } }),
  update: (id: string, data: Record<string, unknown>) => api.put(`/outbound-routes/${id}`, data),
  delete: (id: string) => api.delete(`/outbound-routes/${id}`),
}

export const timeConditionsApi = {
  list: (params?: Record<string, unknown>) => api.get('/time-conditions/', { params }),
  get: (id: string) => api.get(`/time-conditions/${id}`),
  create: (data: Record<string, unknown>, layerId: string, tenantId?: string) =>
    api.post('/time-conditions/', data, { params: { layer_id: layerId, tenant_id: tenantId } }),
  update: (id: string, data: Record<string, unknown>) => api.put(`/time-conditions/${id}`, data),
  delete: (id: string) => api.delete(`/time-conditions/${id}`),
  move: (id: string, targetLayerId: string) => api.patch(`/time-conditions/${id}/move`, null, { params: { target_layer_id: targetLayerId } }),
}

export const timeConditionLayersApi = {
  list: (params?: Record<string, unknown>) => api.get('/time-condition-layers/', { params }),
  get: (id: string) => api.get(`/time-condition-layers/${id}`),
  create: (data: Record<string, unknown>, tenantId?: string) =>
    api.post('/time-condition-layers/', data, { params: { tenant_id: tenantId } }),
  update: (id: string, data: Record<string, unknown>) => api.put(`/time-condition-layers/${id}`, data),
  delete: (id: string) => api.delete(`/time-condition-layers/${id}`),
}

export const holidaysApi = {
  getCantons: () => api.get('/holidays/cantons'),
  getSubdivisions: (canton: string) => api.get('/holidays/subdivisions', { params: { canton } }),
  getConfig: () => api.get('/holidays/config'),
  updateConfig: (data: Record<string, unknown>) => api.put('/holidays/config', data),
  getDefinitions: () => api.get('/holidays/definitions'),
  createDefinition: (data: Record<string, unknown>) => api.post('/holidays/definitions', data),
  updateDefinition: (id: string, data: Record<string, unknown>) => api.put(`/holidays/definitions/${id}`, data),
  deleteDefinition: (id: string) => api.delete(`/holidays/definitions/${id}`),
  preview: (year?: number) => api.get('/holidays/preview', { params: year ? { year } : {} }),
  generate: (data: { time_condition_id: string; years?: number[] }) => api.post('/holidays/generate', data),
  removeGenerated: (tcId: string) => api.delete(`/holidays/generated/${tcId}`),
}

export const conferencesApi = {
  list: (params?: Record<string, unknown>) => api.get('/conferences/', { params }),
  get: (id: string) => api.get(`/conferences/${id}`),
  create: (data: Record<string, unknown>, tenantId?: string) =>
    api.post('/conferences/', data, { params: { tenant_id: tenantId } }),
  update: (id: string, data: Record<string, unknown>) => api.put(`/conferences/${id}`, data),
  delete: (id: string) => api.delete(`/conferences/${id}`),
}

export const phonebookApi = {
  // Entries
  list: (params?: Record<string, unknown>) => api.get('/phonebook/', { params }),
  get: (id: string) => api.get(`/phonebook/${id}`),
  create: (data: Record<string, unknown>, tenantId?: string) =>
    api.post('/phonebook/', data, { params: { tenant_id: tenantId } }),
  update: (id: string, data: Record<string, unknown>) => api.put(`/phonebook/${id}`, data),
  delete: (id: string) => api.delete(`/phonebook/${id}`),
  getGroups: () => api.get('/phonebook/groups'),
  // Sources
  listSources: () => api.get('/phonebook/sources'),
  getSourceTypes: () => api.get('/phonebook/source-types'),
  createSource: (data: Record<string, unknown>) => api.post('/phonebook/sources', data),
  updateSource: (id: string, data: Record<string, unknown>) => api.put(`/phonebook/sources/${id}`, data),
  deleteSource: (id: string) => api.delete(`/phonebook/sources/${id}`),
  syncSource: (id: string) => api.post(`/phonebook/sources/${id}/sync`),
  uploadSourceFile: (id: string, file: File) => {
    const formData = new FormData()
    formData.append('file', file)
    return api.post(`/phonebook/sources/${id}/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
  },
  previewSourceFile: (id: string, file: File) => {
    const formData = new FormData()
    formData.append('file', file)
    return api.post(`/phonebook/sources/${id}/preview`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
  },
  importMappedFile: (id: string, file: File, mapping: Record<string, string>) => {
    const formData = new FormData()
    formData.append('file', file)
    return api.post(`/phonebook/sources/${id}/import-mapped`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      params: { mapping: JSON.stringify(mapping) }
    })
  },
  reorderSources: (priorities: Array<{id: string, priority: number}>) =>
    api.put('/phonebook/sources/reorder', priorities),
  // Microsoft Graph (Exchange Geschäftlich)
  uploadGraphCert: (id: string, file: File) => {
    const formData = new FormData()
    formData.append('file', file)
    return api.post(`/phonebook/sources/${id}/graph-cert`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
  },
  listGraphFolders: (id: string) => api.get(`/phonebook/sources/${id}/graph-folders`),
  generateGraphCert: (id: string, body: { common_name?: string; days?: number }) =>
    api.post(`/phonebook/sources/${id}/graph-cert/generate`, body),
  downloadGraphCert: (id: string) =>
    api.get(`/phonebook/sources/${id}/graph-cert/download`, { responseType: 'blob' }),
  // Telsearch
  lookupTelsearch: (params: {phone?: string, name?: string, city?: string}) =>
    api.get('/phonebook/lookup/telsearch', { params }),
  // Export
  exportYealinkXml: (sourceId?: string) =>
    api.get('/phonebook/export/yealink-xml', { params: { source_id: sourceId }, responseType: 'blob' }),
  exportCsv: (sourceId?: string) =>
    api.get('/phonebook/export/csv', { params: { source_id: sourceId }, responseType: 'blob' }),
  getLdapSchema: () => api.get('/phonebook/export/ldap-schema'),
  // Cleanup
  cleanupDuplicates: (sourceId: string, dryRun: boolean = true) =>
    api.post('/phonebook/cleanup/duplicates', null, { params: { source_id: sourceId, dry_run: dryRun } }),
  // Groundwire/Acrobits contacts URL (for Web Service Contacts)
  getGroundwireContactsUrl: () => {
    const baseUrl = window.location.origin
    return `${baseUrl}/api/phonebook/export/groundwire`
  },
  // Telsearch Stats
  getTelsearchStats: () => api.get('/phonebook/cid-cache/stats'),
  resetTelsearchStats: () => api.delete('/phonebook/cid-cache/stats'),
  getTargetPhonebooks: () => api.get('/phonebook/target-phonebooks'),
}

export const externalDestinationsApi = {
  list: (params?: Record<string, unknown>) => api.get('/external-destinations/', { params }),
  get: (id: string) => api.get(`/external-destinations/${id}`),
  create: (data: Record<string, unknown>, tenantId?: string) =>
    api.post('/external-destinations/', data, tenantId ? { params: { tenant_id: tenantId } } : undefined),
  update: (id: string, data: Record<string, unknown>) => api.put(`/external-destinations/${id}`, data),
  delete: (id: string) => api.delete(`/external-destinations/${id}`),
}

export const voicemailApi = {
  list: (params?: Record<string, unknown>) => api.get('/voicemail/', { params }),
  get: (id: string) => api.get(`/voicemail/${id}`),
  update: (id: string, data: Record<string, unknown>) => api.put(`/voicemail/${id}`, data),
  getMessages: (vmId: string, folder?: string) => api.get(`/voicemail/${vmId}/messages`, { params: { folder } }),
  deleteMessage: (vmId: string, msgId: string, folder?: string) => api.delete(`/voicemail/${vmId}/messages/${msgId}`, { params: { folder } }),
  resendEmail: (vmId: string, msgId: string) => api.post(`/voicemail/${vmId}/messages/${msgId}/resend`),
}

export const parkingApi = {
  list: (params?: Record<string, unknown>) => api.get('/parking/', { params }),
  get: (id: string) => api.get(`/parking/${id}`),
  create: (data: Record<string, unknown>, tenantId?: string) =>
    api.post('/parking/', data, { params: { tenant_id: tenantId } }),
  update: (id: string, data: Record<string, unknown>) => api.put(`/parking/${id}`, data),
  delete: (id: string) => api.delete(`/parking/${id}`),
}

export const pagingApi = {
  list: (params?: Record<string, unknown>) => api.get('/paging/', { params }),
  get: (id: string) => api.get(`/paging/${id}`),
  create: (data: Record<string, unknown>, tenantId?: string) =>
    api.post('/paging/', data, { params: { tenant_id: tenantId } }),
  update: (id: string, data: Record<string, unknown>) => api.put(`/paging/${id}`, data),
  delete: (id: string) => api.delete(`/paging/${id}`),
}

export const blacklistApi = {
  list: (params?: Record<string, unknown>) => api.get('/blacklist/', { params }),
  get: (id: string) => api.get(`/blacklist/${id}`),
  create: (data: Record<string, unknown>, tenantId?: string) =>
    api.post('/blacklist/', data, { params: { tenant_id: tenantId } }),
  update: (id: string, data: Record<string, unknown>) => api.put(`/blacklist/${id}`, data),
  delete: (id: string) => api.delete(`/blacklist/${id}`),
}

export const mohApi = {
  list: (params?: Record<string, unknown>) => api.get('/moh/', { params }),
  get: (id: string) => api.get(`/moh/${id}`),
  create: (data: Record<string, unknown>, tenantId?: string) =>
    api.post('/moh/', data, { params: { tenant_id: tenantId } }),
  update: (id: string, data: Record<string, unknown>) => api.put(`/moh/${id}`, data),
  delete: (id: string) => api.delete(`/moh/${id}`),
  listFiles: (id: string) => api.get(`/moh/${id}/files`),
  getPlayUrl: (id: string, filename: string) => `/api/moh/${id}/files/${encodeURIComponent(filename)}/play`,
  uploadFile: (id: string, file: File) => {
    const formData = new FormData()
    formData.append('file', file)
    return api.post(`/moh/${id}/files/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
  },
  deleteFile: (id: string, filename: string) => api.delete(`/moh/${id}/files/${encodeURIComponent(filename)}`),
}

export const featureCodesApi = {
  list: (params?: Record<string, unknown>) => api.get('/feature-codes/', { params }),
  get: (id: string) => api.get(`/feature-codes/${id}`),
  update: (id: string, data: Record<string, unknown>) => api.put(`/feature-codes/${id}`, data),
}

export const provisioningApi = {
  // Phone models and BLF types
  listModels: () => api.get('/provisioning/models'),
  listBlfTypes: () => api.get('/provisioning/blf-types'),
  // Templates (Vorlagen)
  listTemplates: (params?: Record<string, unknown>) => api.get('/provisioning/templates', { params }),
  getTemplate: (id: string) => api.get(`/provisioning/templates/${id}`),
  createTemplate: (data: Record<string, unknown>) => api.post('/provisioning/templates', data),
  updateTemplate: (id: string, data: Record<string, unknown>) => api.put(`/provisioning/templates/${id}`, data),
  deleteTemplate: (id: string) => api.delete(`/provisioning/templates/${id}`),
  cloneTemplate: (id: string, newName: string) => api.post(`/provisioning/templates/${id}/clone`, { new_name: newName }),
  // Wallpaper
  uploadWallpaper: (templateId: string, formData: FormData) =>
    api.post(`/provisioning/templates/${templateId}/wallpaper`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
  deleteWallpaper: (templateId: string) => api.delete(`/provisioning/templates/${templateId}/wallpaper`),
  // Custom Ringtones
  listRingtones: () => api.get('/provisioning/ringtones'),
  uploadRingtone: (formData: FormData) =>
    api.post('/provisioning/ringtones/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
  deleteRingtone: (filename: string) => api.delete(`/provisioning/ringtones/${encodeURIComponent(filename)}`),
  // MAC Assignments (Zuweisungen)
  listAssignments: (params?: Record<string, unknown>) => api.get('/provisioning/assignments', { params }),
  createAssignment: (data: Record<string, unknown>) => api.post('/provisioning/assignments', data),
  updateAssignment: (id: string, data: Record<string, unknown>, reprovision: boolean = false) =>
    api.put(`/provisioning/assignments/${id}`, data, { params: { reprovision } }),
  deleteAssignment: (id: string) => api.delete(`/provisioning/assignments/${id}`),
  // Re-Provisioning
  reprovision: (id: string, reboot: boolean = false) =>
    api.post(`/provisioning/assignments/${id}/reprovision`, null, { params: { reboot } }),
  // Encryption
  resetEncryption: (id: string) => api.post(`/provisioning/assignments/${id}/reset-encryption`),
  getEncryptionStatus: (id: string) => api.get(`/provisioning/assignments/${id}/encryption-status`),
  // Yealink RPS
  getRPSStatus: () => api.get('/provisioning/rps/status'),
  // Live-Abgleich des lokalen rps_registered-Flags gegen das echte Yealink-Konto
  reconcileRPS: (params?: Record<string, unknown>) => api.get('/provisioning/rps/reconcile', { params }),
  fixRPS: (data: { macs?: string[]; orphan_macs?: string[] }) =>
    api.post('/provisioning/rps/reconcile/fix', data),
  testRPSConnection: () => api.post('/provisioning/rps/test'),
  registerRPS: (id: string) => api.post(`/provisioning/rps/register/${id}`),
  unregisterRPS: (id: string) => api.delete(`/provisioning/rps/register/${id}`),
  resetRPS: (id: string) => api.post(`/provisioning/rps/reset/${id}`),
  // Query RPS status from Yealink API
  getRPSStatusById: (id: string) => api.get(`/provisioning/rps/status/${id}`).then(res => res.data),
  getRPSStatusByMac: (mac: string) => api.get(`/provisioning/rps/status-by-mac/${mac}`).then(res => res.data),
  registerAllRPS: () => api.post('/provisioning/rps/register-all').then(res => res.data),
  // RPS Webhook
  getWebhookUrl: () => api.get('/provisioning/rps/webhook/url'),
  getWebhookLogs: (limit: number = 50) => api.get('/provisioning/rps/webhook/logs', { params: { limit } }),
  // Preview
  previewConfig: (assignmentId: string) => api.get(`/provisioning/assignments/${assignmentId}/preview`),
  previewTemplateConfig: (templateId: string, extensionId?: string) =>
    api.get(`/provisioning/templates/${templateId}/preview`, { params: { extension_id: extensionId } }),
}

export const firmwareApi = {
  // List all firmware files
  list: (params?: { phone_model?: string }) => api.get('/firmware/', { params }),
  // List phone models with active firmware
  listModels: () => api.get('/firmware/models'),
  // Upload new firmware
  upload: (formData: FormData) =>
    api.post('/firmware/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
  // Update firmware metadata
  update: (id: string, data: { is_active?: boolean; description?: string }) =>
    api.patch(`/firmware/${id}`, data),
  // Delete firmware
  delete: (id: string) => api.delete(`/firmware/${id}`),
  // Activate firmware
  activate: (id: string) => api.post(`/firmware/${id}/activate`),
  // Get active firmware for a model
  getActive: (phoneModel: string) => api.get(`/firmware/active/${phoneModel}`),
}

export const nightModeApi = {
  list: (params?: Record<string, unknown>) => api.get('/night-mode/', { params }),
  get: (id: string) => api.get(`/night-mode/${id}`),
  create: (data: Record<string, unknown>, tenantId?: string) =>
    api.post('/night-mode/', data, { params: { tenant_id: tenantId } }),
  update: (id: string, data: Record<string, unknown>) => api.put(`/night-mode/${id}`, data),
  delete: (id: string) => api.delete(`/night-mode/${id}`),
  toggle: (id: string, isEnabled: boolean) => api.post(`/night-mode/${id}/toggle`, { is_enabled: isEnabled }),
  clone: (id: string, data: { new_name: string; new_feature_code?: string }) =>
    api.post(`/night-mode/${id}/clone`, data),
  getActive: (params?: Record<string, unknown>) => api.get('/night-mode/status/active', { params }),
  syncFromPhone: (id: string) => api.post(`/night-mode/${id}/sync`),
}

export const announcementsApi = {
  list: (params?: Record<string, unknown>) => api.get('/announcements/', { params }),
  get: (id: string) => api.get(`/announcements/${id}`),
  create: (data: Record<string, unknown>) => api.post('/announcements/', data),
  update: (id: string, data: Record<string, unknown>) => api.put(`/announcements/${id}`, data),
  delete: (id: string) => api.delete(`/announcements/${id}`),
}

export const destinationsApi = {
  // Get list of destination types
  getTypes: () => api.get('/destinations/types'),
  // Search destinations with optional type filter
  search: (params: { q?: string; type?: string; exclude_type?: string; exclude_id?: string; limit?: number }) =>
    api.get('/destinations/search', { params }),
  // Resolve a destination to get display info
  resolve: (destType: string, destValue: string) =>
    api.get(`/destinations/resolve/${destType}/${destValue}`),
}

export const dashboardApi = {
  // Get active calls in real-time
  getActiveCalls: () => api.get('/dashboard/active-calls'),
  // Get extension number to name lookup
  getExtensionLookup: () => api.get('/dashboard/extension-lookup'),
}

export const faxApi = {
  list: (tenantId?: string) =>
    api.get('/fax/', { params: { tenant_id: tenantId } }).then(r => ({ data: { items: r.data } })),
  get: (id: string) => api.get(`/fax/${id}`),
  create: (data: Record<string, unknown>) => api.post('/fax/', data),
  update: (id: string, data: Record<string, unknown>) => api.put(`/fax/${id}`, data),
  delete: (id: string) => api.delete(`/fax/${id}`),
  getMessages: (id: string, params?: Record<string, unknown>) =>
    api.get(`/fax/${id}/messages`, { params }).then(r => ({ data: { items: r.data } })),
  downloadFax: (messageId: string) =>
    api.get(`/fax/messages/${messageId}/download`, { responseType: 'blob' }),
}

export const softphoneApi = {
  // Check if softphone installers are available
  available: () => api.get('/softphone/available'),
  // Get configuration for an extension (requires auth)
  getConfig: (extensionId: string) => api.get(`/softphone/config/${extensionId}`),
  // Download generic installer (no auth required, config fetched on first run)
  download: async (platform: 'windows' | 'linux' | 'linux-deb' | 'android') => {
    const response = await api.get('/softphone/download', {
      params: { platform },
      responseType: 'blob',
    })
    // Get filename from Content-Disposition header or use default
    const contentDisposition = response.headers['content-disposition']
    let filename = platform === 'windows' ? 'it64PbxPhone-Setup.exe' :
                   platform === 'android' ? 'it64PbxPhone.apk' :
                   platform === 'linux-deb' ? 'it64pbx-phone.deb' : 'it64PbxPhone.AppImage'
    if (contentDisposition) {
      // Starlette sendet bei Namen mit Leerzeichen die RFC-5987-Form
      // (filename*=utf-8''it64PbxPhone%20Setup%201.4.236.exe). Die alte
      // Regex griff auf "filename" ohne den Stern und lieferte deshalb
      // "*=utf-8''it64PbxPhone%20Setup%201.4.236.exe" als Dateinamen —
      // der Browser speicherte unter einem verstuemmelten Namen, in dem die
      // Version nicht mehr erkennbar war. Erweiterte Form zuerst pruefen.
      const extended = contentDisposition.match(/filename\*=(?:utf-8'')?([^;\n]+)/i)
      const plain = contentDisposition.match(/filename="?([^";\n]+)"?/)
      if (extended) {
        try {
          filename = decodeURIComponent(extended[1].trim())
        } catch {
          filename = extended[1].trim()
        }
      } else if (plain) {
        filename = plain[1]
      }
    }
    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]))
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
  },
  // Download config file for an extension (requires auth)
  downloadConfig: async (extensionId: string) => {
    const response = await api.get(`/softphone/download/${extensionId}/config`, {
      responseType: 'blob',
    })
    const url = window.URL.createObjectURL(new Blob([response.data]))
    const link = document.createElement('a')
    link.href = url
    link.download = 'softphone-config.json'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
  },
  // Generate provisioning QR code token
  generateProvisionToken: (extensionId: string, validityMinutes: number = 10) =>
    api.post(`/softphone/provision/${extensionId}/generate`, null, {
      params: { validity_minutes: validityMinutes }
    }),
  // Get QR code image URL
  getProvisionQrUrl: (extensionId: string, token: string) =>
    `/api/softphone/provision/${extensionId}/qr?token=${token}`,
  // Send provisioning email with QR code
  sendProvisionEmail: (extensionId: string, validityMinutes: number = 30) =>
    api.post(`/softphone/provision/${extensionId}/email`, null, {
      params: { validity_minutes: validityMinutes }
    }),
}

export const softphoneProvisioningApi = {
  // Function key types
  getFunctionKeyTypes: () => api.get('/softphone-provisioning/function-key-types'),

  // Templates
  getTemplates: (params?: { skip?: number; limit?: number; search?: string }) =>
    api.get('/softphone-provisioning/templates', { params }),
  getTemplate: (id: string) => api.get(`/softphone-provisioning/templates/${id}`),
  createTemplate: (data: unknown) => api.post('/softphone-provisioning/templates', data),
  updateTemplate: (id: string, data: unknown) => api.put(`/softphone-provisioning/templates/${id}`, data),
  deleteTemplate: (id: string) => api.delete(`/softphone-provisioning/templates/${id}`),
  duplicateTemplate: (id: string) => api.post(`/softphone-provisioning/templates/${id}/duplicate`),

  // Assignments
  getAssignments: (params?: { skip?: number; limit?: number; template_id?: string }) =>
    api.get('/softphone-provisioning/assignments', { params }),
  getAssignment: (extensionId: string) => api.get(`/softphone-provisioning/assignments/${extensionId}`),
  createAssignment: (data: unknown) => api.post('/softphone-provisioning/assignments', data),
  updateAssignment: (extensionId: string, data: unknown) => api.put(`/softphone-provisioning/assignments/${extensionId}`, data),
  deleteAssignment: (extensionId: string) => api.delete(`/softphone-provisioning/assignments/${extensionId}`),
  bulkAssign: (templateId: string, extensionIds: string[]) =>
    api.post('/softphone-provisioning/assignments/bulk', { template_id: templateId, extension_ids: extensionIds }),

  // Geräte-Gesundheit: Version/Rechte pro Gerät + Erreichbarkeit (Wake-Monitor)
  getDeviceHealth: (hours = 168) =>
    api.get('/softphone-provisioning/device-health', { params: { hours } }),

  // Handy-Erfassung: Pro-Gerät-Abweichung (enabled/hours/days null = Vorlagen-Standard)
  setMobileCaptureOverride: (data: {
    domain: string; extension: string; device_id: string;
    enabled: boolean | null; hours: string | null; days: string | null;
  }) => api.put('/softphone-provisioning/mobile-capture-override', data),

  // App-Version-Pins (Canary/Rollback pro SIP-Account)
  getAppPins: () => api.get('/softphone/app-pins'),
  setAppPin: (account: string, data: { version: string; until?: string | null; platform?: 'android' | 'win' }) =>
    api.put(`/softphone/app-pins/${account}`, data),
  deleteAppPin: (account: string, platform: 'android' | 'win' = 'android') =>
    api.delete(`/softphone/app-pins/${account}`, { params: { platform } }),
}

// Gesprächsaufnahme (docs/call-recording.md) — getrennt von recordingsApi (= Ansagen)
export const callRecordingsApi = {
  status: () => api.get('/call-recordings/status'),
  confirmLegal: (text_version: string) => api.post('/call-recordings/legal/confirm', { text_version, accept: true }),
  revokeLegal: () => api.post('/call-recordings/legal/revoke'),
  saveStorage: (data: Record<string, unknown>) => api.put('/call-recordings/storage', data),
  testStorage: () => api.post('/call-recordings/storage/test'),
  deleteStorage: () => api.delete('/call-recordings/storage'),
  list: (params?: Record<string, unknown>) => api.get('/call-recordings', { params }),
  audio: (id: string) => api.get(`/call-recordings/${id}/audio`, { responseType: 'blob' }),
}
